import { useCallback, useEffect, useRef, useState } from 'react';
import { ShopApi } from '@/api/apis/ShopApi';

/**
 * Barnets köp i stjärnbutiken (SHOP-06) — underlaget för ingångarna till "Mina köp":
 * pillerknappen i ChildShopPage och knappen i StarsPage.
 *
 * Hooken svarar på TVÅ frågor, och de styr olika saker:
 * - `hasAnyPurchase` styr om ingången SYNS. Har barnet aldrig köpt något finns det inget
 *   att gå in och titta på, och då ska ingången inte finnas.
 * - `pendingCount` styr vad som STÅR på ingången. Räknaren visar aldrig totalt antal köp —
 *   bara det som faktiskt kräver en handling av en vuxen. En total säger barnet ingenting
 *   och skapar bara en siffra som aldrig går ner.
 *
 * Ett anrop, inte två: `listPurchases({})` UTAN statusfilter ger alla statusar, och de
 * väntande räknas fram i klienten. Ett andra API-anrop bara för att räkna hade kunnat
 * landa ur synk med det första och visa en räknare som inte hör ihop med synligheten.
 *
 * `listPurchases` sväljer fel internt och returnerar undefined. Vi behåller då senast
 * kända värde istället för att nolla: en ingång som försvinner vid ett tillfälligt
 * nätverksfel är värre än en räknare som är någon sekund gammal.
 *
 * TVÅ KAPPLÖPNINGSSKYDD:
 * 1. Varje anrop får ett löpnummer. Landar ett långsamt tidigare svar efter ett snabbare
 *    senare ignoreras det — annars kan räknaren gå BAKÅT till ett äldre värde.
 * 2. Svar som kommer in efter unmount skrivs inte (ingen setState på avmonterad komponent).
 *
 * Läses om när fliken/fönstret blir synligt igen. Utan det står ingången kvar med fel
 * siffra obegränsat länge om föräldern löser in köpet medan barnet har StarsPage eller
 * butiken uppe.
 */
export const usePendingPurchaseCount = (): {
  pendingCount: number;
  hasAnyPurchase: boolean;
  reload: () => Promise<void>;
} => {
  const [pendingCount, setPendingCount] = useState<number>(0);
  // Utgångsläget är "visa inte" tills svaret landat — se kommentaren i StarsPage om blink.
  const [hasAnyPurchase, setHasAnyPurchase] = useState<boolean>(false);
  const latestRequest = useRef<number>(0);
  const mounted = useRef<boolean>(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const reload = useCallback(async () => {
    const requestId = latestRequest.current + 1;
    latestRequest.current = requestId;

    const result = await new ShopApi().listPurchases({});

    // Inte längre det senaste anropet, eller komponenten är borta → släng svaret.
    if (!mounted.current || latestRequest.current !== requestId) return;
    if (result !== undefined) {
      setPendingCount(result.filter((purchase) => purchase.status === 'pending').length);
      setHasAnyPurchase(result.length > 0);
    }
  }, []);

  useEffect(() => {
    reload();
  }, [reload]);

  useEffect(() => {
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') reload();
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => document.removeEventListener('visibilitychange', onVisibilityChange);
  }, [reload]);

  return { pendingCount, hasAnyPurchase, reload };
};
